<script setup lang="ts">
import { computed } from 'vue';
import {
    BCard, BIcon, BSkeletonLoader,
} from '@/design-system';
import { formatNumber } from '@/utils/formatUtils';

type PropsType = {
    title: string;
    value: string | number | null;
    subtitle?: string;
    icon?: string;
    unit?: string;
    iconColor?: string;
    iconBackgroundColor?: string;
    isLoading?: boolean;
    elevation?: string | number;
    color?: string;
    dense?: boolean;
    connected?: boolean;
    disableFormatting?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    elevation: 0,
    isLoading: false,
    dense: false,
    connected: false,
    iconBackgroundColor: 'primary',
});

const cardClass = computed(() => {
    const classes = ['b-stat-card', 'pa-0'];

    if (props.dense) {
        classes.push('b-stat-card--dense');
    }

    if (props.connected) {
        classes.push('b-stat-card--connected');
    }

    return classes.join(' ');
});

const formattedValue = computed(() => formatNumber(props.value));

defineOptions({ inheritAttrs: false });
</script>

<template>
    <BCard
        :elevation="elevation"
        :color="color"
        :class="cardClass"
        text-class="pa-0"
        v-bind="$attrs"
    >
        <BSkeletonLoader
            :loading="isLoading"
            type="list-item-two-line"
        >
            <div class="b-stat-card__content">
                <div
                    v-if="icon"
                    class="b-stat-card__icon-container"
                    :style="{
                        backgroundColor: `rgb(var(--v-theme-${iconBackgroundColor}), 0.1)`,
                    }"
                >
                    <BIcon
                        :icon="icon"
                        :color="iconColor || iconBackgroundColor"
                        size="default"
                        class="b-stat-card__icon"
                    />
                </div>

                <div class="b-stat-card__text-content">
                    <div class="b-stat-card__title">
                        {{ title }}
                    </div>
                    <div class="b-stat-card__value-row">
                        <span class="b-stat-card__value">
                            <template v-if="props.disableFormatting">{{ props.value }}</template>
                            <template v-else>{{ formattedValue }}</template>
                            <template v-if="unit">&nbsp;{{ unit }}</template>
                        </span>
                    </div>
                    <div
                        v-if="subtitle"
                        class="b-stat-card__subtitle"
                    >
                        {{ subtitle }}
                    </div>
                </div>
            </div>
        </BSkeletonLoader>
    </BCard>
</template>

<style lang="scss" scoped>
.b-stat-card {
    height: 100%;
}

.b-stat-card--connected {
    border-radius: 0;

    &:first-child {
        border-radius: 4px 0 0 4px;
    }

    &:last-child {
        border-radius: 0 4px 4px 0;
    }

    &:only-child {
        border-radius: 4px;
    }
}

.b-stat-card--dense .b-stat-card__content {
    padding: 0;
}

.b-stat-card__content {
    display: flex;
    align-items: center;
    gap: 16px;
    height: 100%;
    padding: 16px;
}

.b-stat-card__icon-container {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.b-stat-card__icon {
    flex-shrink: 0;
}

.b-stat-card__text-content {
    flex: 1;
    min-width: 0;
}

.b-stat-card__title {
    font-size: 0.75rem;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
}

.b-stat-card__value-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.b-stat-card__value {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
    color: rgb(var(--v-theme-on-surface));
}

.b-stat-card__subtitle {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    margin-top: 2px;
}

.b-stat-card--dense {
    .b-stat-card__content {
        padding: 10px 12px;
        gap: 12px;
    }

    .b-stat-card__icon-container {
        width: 40px;
        height: 40px;
    }

    .b-stat-card__value {
        font-size: 1.25rem;
        font-weight: 600;
    }

    .b-stat-card__title {
        font-size: 0.7rem;
        margin-bottom: 2px;
    }
}

@media (max-width: 768px) {
    .b-stat-card--connected {
        flex: 1;
        min-width: 0;

        .b-stat-card__content {
            flex-direction: column;
            text-align: center;
            gap: 8px;
        }

        .b-stat-card__text-content {
            align-items: center;
        }

        .b-stat-card__value-row {
            flex-direction: column;
            gap: 4px;
        }
    }
}
</style>
